"use client";

import { ChevronDown, ChevronRight } from "lucide-react";
import { useState } from "react";

interface ContextTreeProps {
  data: Record<string, unknown>;
  level?: number;
}

export function ContextTree({ data, level = 0 }: ContextTreeProps) {
  return (
    <div className="space-y-0.5 h-full">
      {Object.entries(data).map(([key, value]) => (
        <ContextTreeNode key={key} keyName={key} value={value} level={level} />
      ))}
    </div>
  );
}

interface ContextTreeNodeProps {
  keyName: string;
  value: unknown;
  level: number;
}

function ContextTreeNode({ keyName, value, level }: ContextTreeNodeProps) {
  const [isExpanded, setIsExpanded] = useState(level < 1); // Auto-expand first level

  const isObject =
    value !== null && typeof value === "object" && !Array.isArray(value);
  const isArray = Array.isArray(value);
  const isExpandable = isObject || isArray;

  const paddingLeft = level * 16;

  // Format simple values
  const formatValue = (val: unknown): string => {
    if (val === null) return "null";
    if (val === undefined) return "undefined";
    if (typeof val === "string") return `"${val}"`;
    if (typeof val === "boolean") return val ? "true" : "false";
    if (typeof val === "number") return String(val);
    return String(val);
  };

  // Get color for value type
  const getValueColor = (val: unknown): string => {
    if (val === null || val === undefined) return "text-gray-400";
    if (typeof val === "string") return "text-green-600";
    if (typeof val === "boolean") return "text-purple-600";
    if (typeof val === "number") return "text-blue-600";
    return "text-gray-700";
  };

  return (
    <div>
      {/* biome-ignore lint/a11y/useKeyWithClickEvents: <explanation> */}
      <div
        className="flex items-start gap-1 py-0.5 hover:bg-gray-100 rounded px-1 cursor-pointer"
        style={{ paddingLeft: `${paddingLeft}px` }}
        onClick={() => isExpandable && setIsExpanded(!isExpanded)}
      >
        {/* Expand/Collapse Icon */}
        {isExpandable ? (
          isExpanded ? (
            <ChevronDown className="h-3 w-3 text-gray-500 mt-0.5 flex-shrink-0" />
          ) : (
            <ChevronRight className="h-3 w-3 text-gray-500 mt-0.5 flex-shrink-0" />
          )
        ) : (
          <span className="w-3 flex-shrink-0" />
        )}

        {/* Key */}
        <span className="text-xs font-medium text-gray-700 flex-shrink-0">
          {keyName}:
        </span>

        {/* Value or Preview */}
        {!isExpandable && (
          <span className={`text-xs font-mono ${getValueColor(value)}`}>
            {formatValue(value)}
          </span>
        )}
        {isExpandable && !isExpanded && (
          <span className="text-xs text-gray-400 font-mono">
            {isArray
              ? `[${(value as unknown[]).length}]`
              : `{${Object.keys(value as object).length}}`}
          </span>
        )}
      </div>

      {/* Expanded Children */}
      {isExpandable && isExpanded && (
        <div>
          {isArray ? (
            (value as unknown[]).map((item, index) => (
              <ContextTreeNode
                // biome-ignore lint/suspicious/noArrayIndexKey: @FIXME:
                key={index}
                keyName={String(index)}
                value={item}
                level={level + 1}
              />
            ))
          ) : (
            <ContextTree
              data={value as Record<string, unknown>}
              level={level + 1}
            />
          )}
        </div>
      )}
    </div>
  );
}
